import Axios from 'axios'
import { userService } from '@/services/api'
import { groupByArrayOfObject } from '@/common/arrays'
import { UserStatus } from '@/constants'
import { defineAbilityFor } from '@/ability'
import { getToken } from '@/services/jwt'

const initialStates = {
    status: 'LOADING',
    current: {},
    usersList: {},
    rulesList: {},
    token: getToken(),
    abilityUpdated: false,
    pagination: {
        no_paginate: false,
        per_page: 20,
        current_page: 1,
        last_page: 1,
    },
    params: {
        page: 1
    },
    login_as_token: false
}

const mutations = {
    loading(state) {
        state.status = 'LOADING'
    },
    setCurrentUser(state, current) {
        state.status = 'SET_CURRENT_USER'
        state.current = current
    },
    abilityUpdated(state, abilityUpdated) {
        // state.status  = 'ABBILITY_UPDATED'
        state.abilityUpdated = abilityUpdated
    },
    setUsersList(state, users) {
        state.status = 'SET_LIST'
        state.usersList = users
    },
    setSingleUser(state, user) {
        state.status = 'SET_SINGLE'
        state.usersList[user.id] = Object.assign({}, user, {
            fetch_find: true,
        })
    },
    setRulesList(state, rulesList) {
        state.status = 'SET_RULES_LIST'
        state.rulesList = rulesList
    },
    setPagination(state, pagination) {
        state.status = 'SET_PAGINATION'
        state.pagination = Object.assign({}, state.pagination, pagination)
    },
    setParams(state, params) {
        state.status = 'SET_PARAMS'
        state.params = Object.assign({}, state.params, params)
    },
    resetParams(state) {
        state.status = 'RESET_PARAMS'
        state.params = {
            page: 1
        }
    },
    setLoginAsToken(state, token) {
        state.status = 'SET_LOGIN_AS_TOKEN'
        state.login_as_token = token
    },
    createUser(state, user) {
        state.status = 'CREATE'
        state.usersList = Object.assign({[user.id]: user}, state.usersList)
    },
    updateUser(state, user) {
        state.status = 'UPDATE'
        state.usersList[user.id] = Object.assign({}, state.usersList[user.id], user)
    },
    updateCurrentUser(state, user) {
        state.status = 'UPDATE'
        state.current = Object.assign({}, state.current, user)
    },
    deleteUser(state, id) {
        state.status = 'DELETE'
        state.usersList[id] = Object.assign({}, state.usersList[id], {
            status: state.usersList[id].status === UserStatus.PENDING
                ? UserStatus.REMOVED_WHEN_PENDING
                : UserStatus.REMOVED
        })
    },
    restoreUser(state, id) {
        state.status= 'RESTORE'
        state.usersList[id] = Object.assign({}, state.usersList[id], {
            status: state.usersList[id].status === UserStatus.REMOVED_WHEN_PENDING
                ? UserStatus.PENDING
                : UserStatus.ACTIVE
        })
    },
    error(state) {
        state.status = 'ERROR'
    },
    changeCurrentUserSetupStatus(state, setup_status) {
        state.current.setup_status = setup_status
    },
    currentUserAgreement(state, agreement) {
        state.current.agreement = agreement
    },
    clearFetched(state) {
        state.usersList = {}
    }
}

const getters = {
    current: state => state.current,
    status: state => state.status,
    is_current_fetched: state => state.current.id !== undefined,
    abilityUpdated: ({ abilityUpdated }) => abilityUpdated,
    hasRole: state => (role) => state.current.roles && state.current.roles.includes(role),
    users: state => state.usersList,
    userById: state => id => state.usersList[id],
    params: state => state.params,
    pagination: state => state.pagination,
    user: state => state.user,
    rulesList: state => state.rulesList,
    has_valid_media: state => state.current.has_valid_media,
    has_active_store: state => state.current.has_active_store,
    login_as_token: state => state.login_as_token,
    selectableUsers: (state, getters) => {
        return Object.keys(getters.users).map((id) => {
            const user = getters.users[id]
            return {
                value: id,
                label: user.username ? `${user.id}-${user.full_name}(${user.username})` : `${user.id}-${user.full_name}`
            }
        })
    },
    selectableUsersWithNullStart: (state, getters) => {
        return [
            {
                value: '',
                label: '',
            },
            ...getters.selectableUsers
        ]
    },
    isLoggedIn: state => !!state.token
}

const actions = {
    setParamsAndFetch({dispatch}, params) {
        dispatch('setParams', params)
        dispatch('fetch')
    },
    setParams({commit}, params) {
        commit('setParams', params)
    },
    resetParams({commit}) {
        commit('resetParams')
    },
    fetchCurrent({ commit, rootGetters }) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.current().then(response => {
                commit('setCurrentUser', response.data.data)

                // update ability
                const ability = rootGetters.ability
                ability.update(defineAbilityFor(response.data.data))
                commit('abilityUpdated', true)
                resolve()
            }).catch(() => {
                localStorage.removeItem('accessToken')
                delete Axios.defaults.headers.common['Authorization']
                reject()
            })
        })
    },
    fetch({ commit, state }, params = {}) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            userService.fetch(params)
                .then(response => {
                    const result = response.data
                    const users = groupByArrayOfObject(result.data, 'id')

                    commit('setUsersList', users)
                    commit('setPagination', {
                        per_page: result.per_page,
                        current_page: result.current_page,
                        last_page: result.last_page,
                    })
                    commit('setParams', {
                        page: result.current_page,
                    })
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    fetchForSelect({ commit, state }, params = {}) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            userService.fetchForSelect(params)
                .then(response => {
                    const result = response.data
                    const users = groupByArrayOfObject(result.data, 'id')

                    commit('setUsersList', users)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    find({ commit }, hashid) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.find(hashid)
                .then(response => {
                    const result = response.data
                    const user = result.data
                    commit('setSingleUser', user)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    clearFetched({commit}) {
        commit('clearFetched')
    },
    abilityUpdated({ commit }) {
        commit('abilityUpdated', true)
    },
    loginAs({ commit }, hashid) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.loginAs(hashid)
                .then(response => {
                    const result = response.data

                    localStorage.setItem('ownerAccessToken', localStorage.getItem('accessToken'))
                    localStorage.setItem('accessToken', result.data.api_token)

                    resolve(result)

                    location.replace('/')
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    create({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.create(params)
                .then(response => {
                    const result = response.data

                    commit('createUser', result.data)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    edit({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.update(params, params.id)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    updateFinancial({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.updateFinancial(params, params.id)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    updateCurrentUserFinancial({commit, state}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.updateFinancial(params, state.current.id)
                .then(() => {
                    commit('updateCurrentUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeStatus({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.changeStatus(params.id, params.status)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeSetupStatus({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.changeSetupStatus(params.id, params)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeResponsible({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.changeResponsible(params.id, params.responsible)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeStatus({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.changeStatus(params.id, params.activity_status)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeCurrentUserSetupStatus({ commit, state }, setup_status) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.changeSetupStatus(state.current.id, {
                setup_status: setup_status
            })
                .then(() => {
                    commit('updateCurrentUser', {
                        setup_status: setup_status
                    })
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    agreement({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.agreement(params.id, params.agreement)
                .then(() => {
                    commit('updateUser', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    currentUserAgreement({ commit, state }, agreement) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.agreement(state.current.id, agreement)
                .then(() => {
                    commit('updateCurrentUser', {
                        agreement: agreement
                    })
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    userAgreement({ commit, state }, data) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.agreement(data.user_id, data.agreement)
                .then(() => {
                    commit('updateUser', {
                        id: data.user_id,
                        agreement: data.agreement
                    })
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error)
                })
        })
    },
    delete({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.delete(params.id, params)
                .then(() => {
                    commit('deleteUser', params.id)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    restore({commit}, hashid) {
        commit('loading')

        return new Promise((resolve, reject) => {
            userService.restore(hashid)
                .then(() => {
                    commit('restoreUser', hashid)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    decreaseUnreadNotifications({commit, state}) {
        state.current.unread_notifications = state.current.unread_notifications - 1;
    }
}

export default {
    namespaced: true,
    state: initialStates,
    mutations: mutations,
    getters: getters,
    actions: actions,
}